/**
 * Eligibility client — mirrors `apps/api/src/domains/eligibility/routes.ts`
 * verbatim.
 *
 * TWO AUDIENCES, TWO DOORS, AND THIS MODULE KEEPS THEM APART.
 *
 *   {@link me}      — the worker's own state. Two fields, ever.
 *   everything else — management. Reasons, scopes, history, impact.
 *
 * The server refuses to vary one body by permission (see the route file's
 * header), and this module refuses to offer one function that returns either.
 * `me()` returns `SelfEligibility`, a two-field type with no optional
 * management field on it, so there is no shape a worker screen could widen into
 * by adding a property access.
 *
 * WHAT THE PERMISSIONS ARE, AND THAT THEY ARE BORROWED. The server's constants
 * are `directory:read` (read), `directory:read_all` (organization-wide reach)
 * and `directory:manage` (place, lift, history, reasons, policy). They are
 * INTERIM — the domain proposes `eligibility:*` keys it has not been given —
 * and they are re-exported here rather than retyped so a screen's gate and the
 * route's gate cannot drift.
 *
 * THE FOUR REASON FIELDS ARE OPTIONAL, NOT NULLABLE, AND THAT IS THE CONTRACT.
 * `reasonCategory`, `reasonLabel`, `managementReason` and `liftReason` are
 * ABSENT from a hold read without `directory:manage`, and `reasonWithheld` says
 * which of the two happened. A screen that renders `hold.managementReason ??
 * 'none'` has collapsed withheld into absent — read `reasonWithheld` first.
 * See `docs/withholding-contract.md` notation (B).
 */
import { get, post, put } from './transport.js';
/**
 * The permission the server checks, re-exported so a screen gates on the same
 * string the route does. Interim/borrowed keys — see the module header.
 */
export const ELIGIBILITY_READ = 'directory:read';
export const ELIGIBILITY_READ_ALL = 'directory:read_all';
export const ELIGIBILITY_MANAGE = 'directory:manage';
/**
 * The requirement-shaped categories: things the platform can observe, and
 * therefore the only ones a hold may restore from automatically.
 *
 * `eligibility_hold_restoration_ck` (migration 1758110000000) enforces the
 * split in SQL, so this is not a UI convenience — a management-shaped category
 * submitted with automatic restoration is refused by the database.
 */
export const REQUIREMENT_REASON_CATEGORIES = Object.freeze([
    'credential_requirement',
    'supervision_requirement',
    'onboarding_requirement',
    'missing_program_prerequisite',
]);
/** Decisions only a person makes. Always `management_only`. */
export const MANAGEMENT_REASON_CATEGORIES = Object.freeze([
    'administrative_hold',
    'investigation',
    'performance_hold',
    'hr_management',
    'other',
]);
export const REASON_CATEGORIES = Object.freeze([
    ...REQUIREMENT_REASON_CATEGORIES,
    ...MANAGEMENT_REASON_CATEGORIES,
]);
/**
 * The client's copy of `mayRestoreAutomatically` — and of
 * `eligibility_hold_restoration_ck`.
 *
 * It exists so a form can refuse the impossible combination with a sentence
 * instead of letting the server answer 23514, and it is derived from
 * `REQUIREMENT_REASON_CATEGORIES` rather than retyped, so the two lists cannot
 * disagree.
 */
export function mayRestoreAutomatically(category) {
    return REQUIREMENT_REASON_CATEGORIES.includes(category);
}
/**
 * `GET /eligibility/me` — the caller's own state. No permission beyond being
 * signed in.
 *
 * ON THE `ALWAYS_ALLOWED` LIST IN THE GUARD, so it answers while the caller is
 * restricted. That is the point: this is the one read a held worker can still
 * make, and without it the restricted app is a blank screen with no explanation
 * in it.
 */
export function me(options) {
    return get('/eligibility/me', options);
}
/**
 * `GET /eligibility/holds` — needs `directory:read`; widened to the whole
 * organization by `directory:read_all`, and the reason fields need
 * `directory:manage`.
 *
 * `status` defaults to `active` ON THE SERVER. An omitted filter is therefore
 * NOT "every hold ever" — pass `{ status: 'lifted' }` for the history side.
 */
export function holds(query = {}, options) {
    return get('/eligibility/holds', { ...options, query: { ...query } });
}
/**
 * `GET /eligibility/workers/:workerId` — needs `directory:read`.
 *
 * ANSWERS 404 FOR A WORKER OUTSIDE THE CALLER'S REACH, in the same words an
 * unknown uuid gets, deliberately: a 403 here would confirm the id names a real
 * colleague, which is a workforce enumeration through the eligibility door. So
 * a `NotFoundError` from this call is NOT evidence the worker does not exist.
 */
export function worker(workerId, options) {
    return get(`/eligibility/workers/${workerId}`, options);
}
/**
 * `GET /eligibility/workers/:workerId/history` — needs `directory:manage`.
 * Every row carries a reason, so there is no read-only projection of it.
 */
export function history(workerId, options) {
    return get(`/eligibility/workers/${workerId}/history`, options);
}
/** `POST /eligibility/holds` → 201. Needs `directory:manage`. */
export async function placeHold(input, options) {
    const body = await post('/eligibility/holds', input, options);
    return body.hold;
}
/**
 * `POST /eligibility/holds/:id/lift` — needs `directory:manage`.
 *
 * DELIBERATELY NOT A DELETE, and not a PATCH that sets a status. A hold is
 * never removed and never edited: it is lifted, which is an event with an
 * author and a reason, and the row keeps saying the restriction happened.
 * `reason` is required and 1–1000 characters — there is no unexplained lift.
 */
export async function liftHold(id, input, options) {
    const body = await post(`/eligibility/holds/${id}/lift`, input, options);
    return body.hold;
}
/** `GET /eligibility/policy` — needs `directory:read`. */
export async function policy(options) {
    const body = await get('/eligibility/policy', options);
    return body.policy;
}
/**
 * `PUT /eligibility/policy` — needs `directory:manage`.
 *
 * All three fields are REQUIRED by the server's `.strict()` body: this is a
 * replacement, not a patch, so a screen must send the values it did not change.
 */
export async function setPolicy(input, options) {
    const body = await put('/eligibility/policy', input, options);
    return body.policy;
}
//# sourceMappingURL=eligibility.js.map